import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useLang, ui, tx } from "../../i18n";
import { Icon } from "../Icon";
import { Marquee } from "../Marquee";

const POINT = "18.4655,-66.1057";
const CACHE_KEY = "ppr-weather-v1";

const PLACEHOLDER_PERIODS = [
  { name: "Hoy / Today", temperature: 88, temperatureUnit: "F", windSpeed: "10 mph", shortForecast: "Parcialmente nublado / Partly cloudy", detailedForecast: "" },
  { name: "Esta noche / Tonight", temperature: 77, temperatureUnit: "F", windSpeed: "8 mph", shortForecast: "Aguaceros pasajeros / Passing showers", detailedForecast: "" },
  { name: "Mañana / Tomorrow", temperature: 89, temperatureUnit: "F", windSpeed: "12 mph", shortForecast: "Sol con aguaceros / Sun with showers", detailedForecast: "" },
];

async function nwsGet(url, lang) {
  const r = await fetch(url, {
    headers: {
      Accept: "application/geo+json;version=1",
      "Accept-Language": lang === "es" ? "es,en-US;q=0.8,en;q=0.6" : "en-US,en;q=0.8",
    },
    signal: AbortSignal.timeout(8000),
  });
  if (!r.ok) throw new Error(`NWS ${r.status}`);
  return r.json();
}

async function loadWeather(lang) {
  try {
    const meta = await nwsGet(`https://api.weather.gov/points/${POINT}`, lang);
    const [forecast, alerts] = await Promise.all([
      nwsGet(meta.properties.forecast, lang),
      nwsGet("https://api.weather.gov/alerts/active?area=PR", lang),
    ]);
    const value = { source: "nws", fetchedAt: new Date().toISOString(), periods: forecast.properties.periods, alerts: alerts.features || [] };
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(value)); } catch {}
    return value;
  } catch {
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/weather?language=${lang}`, { signal: AbortSignal.timeout(8000) });
      if (r.ok) {
        const d = await r.json();
        return { source: d.source === "nws" ? "nws" : "cache", fetchedAt: d.fetchedAt, periods: d.forecast.properties.periods, alerts: d.alerts.features || [] };
      }
    } catch {}
    try {
      const c = localStorage.getItem(CACHE_KEY);
      if (c) return { ...JSON.parse(c), source: "cache" };
    } catch {}
    return { source: "placeholder", fetchedAt: null, periods: PLACEHOLDER_PERIODS, alerts: [] };
  }
}

const S = ui.weather.status;

// Registro local de simulacros completados (para la tarjeta "Tu Preparación")
export function logDrill(type = "tsunami") {
  try {
    const arr = JSON.parse(localStorage.getItem("ppr-drills") || "[]");
    arr.push({ ts: new Date().toISOString(), type });
    localStorage.setItem("ppr-drills", JSON.stringify(arr.slice(-50)));
  } catch { /* almacenamiento lleno o bloqueado */ }
}

// Sirena sintetizada (WebAudio) — suena cuando llega un aviso NWS nuevo mientras se navega
function playSiren() {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return;
  try {
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    const t = ctx.currentTime;
    for (let i = 0; i < 5; i++) {
      osc.frequency.setValueAtTime(650, t + i * 0.6);
      osc.frequency.linearRampToValueAtTime(920, t + i * 0.6 + 0.3);
      osc.frequency.linearRampToValueAtTime(650, t + i * 0.6 + 0.6);
    }
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 3);
    osc.onended = () => ctx.close();
  } catch { /* audio no disponible sin interacción del usuario */ }
}

function waAlertText(a, lang) {
  const p = a.properties || {};
  const head = (p.headline || (p.description || "").slice(0, 300)).slice(0, 350);
  return lang === "es"
    ? `⚠️ AVISO NWS — Puerto Rico\n${p.event || ""}\n${head}\n\nVía preparatepr.org — Puerto Rico, Preparado Siempre`
    : `⚠️ NWS ALERT — Puerto Rico\n${p.event || ""}\n${head}\n\nVia preparatepr.org — Puerto Rico, Always Prepared`;
}

function waForecastText(periods, alerts, lang) {
  const lines = periods
    .slice(0, 3)
    .map((p) => `${p.name}: ${p.temperature}°${p.temperatureUnit} — ${p.shortForecast} — ${lang === "es" ? "Viento" : "Wind"}: ${p.windSpeed}`);
  const names = [...new Set(alerts.map((a) => a.properties?.event).filter(Boolean))];
  const alertLine = names.length
    ? lang === "es" ? `\nAvisos activos: ${names.join(", ")}` : `\nActive alerts: ${names.join(", ")}`
    : lang === "es" ? "\nSin avisos activos." : "\nNo active alerts.";
  return lang === "es"
    ? `PRONÓSTICO NWS — Puerto Rico\n${lines.join("\n")}${alertLine}\n\nVía preparatepr.org — Puerto Rico, Preparado Siempre`
    : `NWS FORECAST — Puerto Rico\n${lines.join("\n")}${alertLine}\n\nVia preparatepr.org — Puerto Rico, Always Prepared`;
}

function StatusCard({ testId, icon, title, big, sub, tone }) {
  const tones = {
    red: "border-red-600 bg-red-50",
    amber: "border-amber-600 bg-amber-50",
    green: "border-green-700 bg-green-50",
    navy: "border-[#1C2B4A] bg-white",
  };
  const iconTones = { red: "text-red-600", amber: "text-amber-700", green: "text-green-700", navy: "text-[#1C2B4A]" };
  return (
    <motion.div
      data-testid={testId}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5 }}
      className={`rounded-lg border-l-4 border border-slate-300 p-5 sm:p-6 ${tones[tone]}`}
    >
      <div className="flex items-center gap-2.5 mb-3">
        <Icon name={icon} className={`w-6 h-6 ${iconTones[tone]}`} />
        <h3 className="font-display font-bold text-lg text-slate-900">{title}</h3>
      </div>
      <p className="text-2xl font-black font-display tracking-tight text-slate-900">{big}</p>
      <p className="mt-1.5 text-sm text-slate-600 leading-snug">{sub}</p>
    </motion.div>
  );
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export function ClimaTab({ lowBandwidth }) {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushState, setPushState] = useState(() =>
    typeof window !== "undefined" && localStorage.getItem("ppr-push") === "1" ? "on" : "off"
  );
  const [sirenOn, setSirenOn] = useState(() => localStorage.getItem("ppr-siren") !== "0");
  const [newAlert, setNewAlert] = useState(false);
  const [tsunamiDrill, setTsunamiDrill] = useState(false);
  const sirenRef = useRef(sirenOn);
  sirenRef.current = sirenOn;
  const seededRef = useRef(false);

  const toggleSiren = () => {
    const next = !sirenOn;
    setSirenOn(next);
    localStorage.setItem("ppr-siren", next ? "1" : "0");
    if (next) playSiren();
  };

  const enablePush = async () => {
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      setPushState("unsupported");
      return;
    }
    setPushState("busy");
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { setPushState("denied"); return; }
      const reg = await navigator.serviceWorker.ready;
      const { key } = await (await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/vapid-key`)).json();
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(key) });
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
      localStorage.setItem("ppr-push", "1");
      setPushState("on");
    } catch {
      setPushState("error");
    }
  };

  const disablePush = async () => {
    setPushState("busy");
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/push/subscribe`, {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ subscription: sub.toJSON() }),
        });
        await sub.unsubscribe();
      }
      localStorage.removeItem("ppr-push");
      setPushState("off");
    } catch {
      setPushState("error");
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    const d = await loadWeather(lang);
    setData(d);
    setLoading(false);
    // Siembra en cada montaje de Clima: los avisos ya activos NO suenan; solo suenan los NUEVOS al navegar
    if (!seededRef.current) {
      seededRef.current = true;
      try {
        localStorage.setItem(
          "ppr-seen-alerts",
          JSON.stringify((d.alerts || []).map((a) => a.properties?.id || a.id).filter(Boolean))
        );
      } catch { /* almacenamiento lleno o bloqueado */ }
    }
  }, [lang]);

  useEffect(() => { refresh(); }, [refresh]);

  // Vigilancia cada 5 min: si aparece un aviso NWS nuevo, banner + sirena
  useEffect(() => {
    const iv = setInterval(async () => {
      const fresh = await loadWeather(lang);
      const ids = (fresh.alerts || []).map((a) => a.properties?.id || a.id).filter(Boolean);
      let seen = [];
      try { seen = JSON.parse(localStorage.getItem("ppr-seen-alerts") || "[]"); } catch { seen = []; }
      try { localStorage.setItem("ppr-seen-alerts", JSON.stringify(ids)); } catch { /* sin espacio */ }
      if (ids.some((id) => !seen.includes(id))) {
        setData(fresh);
        setNewAlert(true);
        if (sirenRef.current) playSiren();
      }
    }, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [lang]);

  const periods = data?.periods || [];
  const alerts = data?.alerts || [];
  const tsunamiAlerts = alerts.filter((a) => /tsunami/i.test(a.properties?.event || ""));
  const showTsunami = tsunamiDrill || tsunamiAlerts.length > 0;

  const toggleDrill = () => {
    if (tsunamiDrill) {
      logDrill("tsunami");
      setTsunamiDrill(false);
    } else {
      setTsunamiDrill(true);
      if (sirenRef.current) playSiren();
    }
  };

  const endDrill = () => {
    if (tsunamiDrill) logDrill("tsunami");
    setTsunamiDrill(false);
  };
  const hurricaneAlerts = alerts.filter((a) => /hurricane|tropical|storm/i.test(a.properties?.event || ""));
  const floodAlerts = alerts.filter((a) => /flood/i.test(a.properties?.event || ""));
  const now = new Date();
  const seasonOn = now.getMonth() >= 5 && now.getMonth() <= 10;

  const sourceBadge = data?.source === "nws"
    ? { label: tx(ui.weather.live, lang), cls: "bg-green-700 text-white" }
    : data?.source === "cache"
      ? { label: tx(ui.weather.cache, lang), cls: "bg-amber-600 text-white" }
      : { label: tx(ui.weather.placeholder, lang), cls: "bg-slate-500 text-white" };

  return (
    <div data-testid="clima-tab">
      <section className="px-4 sm:px-8 lg:px-12 py-8 sm:py-10">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">{tx(ui.weather.title, lang)}</h2>
            <p className="mt-1 text-sm sm:text-base text-slate-600">{tx(ui.weather.subtitle, lang)}</p>
          </div>
          <div className="flex items-center gap-3">
            {data && (
              <span data-testid="weather-source-badge" className={`rounded-full px-3 py-1 text-xs font-bold ${sourceBadge.cls}`}>
                {sourceBadge.label}
              </span>
            )}
            <button
              data-testid="weather-refresh-btn"
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-md border-2 border-[#1C2B4A] px-4 py-2 text-sm font-bold text-[#1C2B4A] hover:bg-[#1C2B4A] hover:text-white transition-colors duration-200 disabled:opacity-50 active:scale-[0.98]"
            >
              <Icon name="RefreshCw" className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              {tx(ui.weather.refresh, lang)}
            </button>
            {periods.length > 0 && (
              <a
                data-testid="forecast-share-wa"
                href={`https://wa.me/?text=${encodeURIComponent(waForecastText(periods, alerts, lang))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border-2 border-green-700 px-4 py-2 text-sm font-bold text-green-700 hover:bg-green-700 hover:text-white transition-colors duration-200 active:scale-[0.98]"
              >
                <Icon name="MessageCircle" className="w-4 h-4" />
                {tx(ui.weather.shareForecast, lang)}
              </a>
            )}
          </div>
        </div>

        {data?.fetchedAt && (
          <p data-testid="weather-updated" className="mb-4 font-mono-pr text-xs text-slate-500">
            {tx(ui.weather.updated, lang)}: {new Date(data.fetchedAt).toLocaleString(lang === "es" ? "es-PR" : "en-US")}
          </p>
        )}

        <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
          <div data-testid="current-forecast-card" className="lg:col-span-1 rounded-lg border border-slate-300 bg-[#1C2B4A] text-white p-6 sm:p-8">
            {loading && !data ? (
              <p className="text-slate-300 animate-pulse">…</p>
            ) : periods[0] ? (
              <>
                <p className="font-mono-pr text-xs font-bold uppercase tracking-[0.2em] text-amber-500">{periods[0].name}</p>
                <p className="mt-3 font-display text-6xl sm:text-7xl font-black tracking-tighter">
                  {periods[0].temperature}°<span className="text-3xl text-slate-300">{periods[0].temperatureUnit}</span>
                </p>
                <p className="mt-3 text-lg font-semibold">{periods[0].shortForecast}</p>
                <p className="mt-2 flex items-center gap-2 text-sm text-slate-300">
                  <Icon name="Wind" className="w-4 h-4" /> {tx(ui.weather.wind, lang)}: {periods[0].windSpeed}
                </p>
              </>
            ) : null}
          </div>

          <div className="lg:col-span-2 rounded-lg border border-slate-300 bg-white p-6 sm:p-8">
            <h3 data-testid="weekly-forecast-title" className="font-mono-pr text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4">
              {tx(ui.weather.weekly, lang)}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {(loading && !data ? [] : periods.slice(1, 7)).map((p, i) => (
                <div key={i} data-testid={`forecast-period-${i}`} className="rounded-md border border-slate-200 bg-[#F8FAFC] p-4 transition-colors duration-200 hover:border-amber-500">
                  <p className="text-xs font-bold text-slate-500 truncate">{p.name}</p>
                  <p className="mt-1 font-display text-2xl font-black text-slate-900">{p.temperature}°{p.temperatureUnit}</p>
                  <p className="mt-1 text-xs text-slate-600 leading-snug line-clamp-2">{p.shortForecast}</p>
                </div>
              ))}
              {loading && !data && <p className="text-slate-400 text-sm animate-pulse col-span-full">Cargando / Loading…</p>}
            </div>
            <p className="mt-4 text-xs text-slate-400">{tx(ui.weather.nwsNote, lang)}</p>
          </div>
        </div>

        <div className="mt-8">
          {showTsunami && (
            <div data-testid="tsunami-banner" role="alert" className="mb-4 rounded-lg border-4 border-red-800 bg-red-700 p-5 text-white shadow-xl">
              {tsunamiDrill && (
                <div className="mb-2 flex items-center justify-between gap-2">
                  <span data-testid="tsunami-drill-tag" className="rounded bg-white px-2 py-0.5 font-mono-pr text-xs font-black tracking-widest text-red-700">
                    {lang === "es" ? "SIMULACRO" : "DRILL"}
                  </span>
                  <button
                    data-testid="tsunami-drill-end"
                    onClick={endDrill}
                    className="rounded border border-white/60 px-2.5 py-1 text-xs font-bold hover:bg-white hover:text-red-700 transition-colors duration-150"
                  >
                    {lang === "es" ? "Terminar simulacro" : "End drill"}
                  </button>
                </div>
              )}
              <p className="flex items-center gap-2 font-display text-xl sm:text-2xl font-black tracking-tight">
                <Icon name="Waves" className="w-7 h-7 shrink-0" />
                {lang === "es" ? "ALERTA DE TSUNAMI — ALÉJESE DE LA COSTA" : "TSUNAMI ALERT — MOVE AWAY FROM THE COAST"}
              </p>
              <p className="mt-2 text-sm sm:text-base font-semibold leading-relaxed">
                {lang === "es"
                  ? "Camine a terreno alto (100+ pies de elevación o 1 milla tierra adentro) o suba a un edificio de hormigón de 3+ pisos. Si sintió un sismo fuerte, NO espere una orden oficial. No regrese a la costa hasta el aviso oficial: el tsunami llega en varias olas."
                  : "Walk to high ground (100+ ft elevation or 1 mile inland) or go up a concrete building 3+ stories. If you felt a strong quake, DO NOT wait for an official order. Do not return to the coast until the official all-clear: tsunamis arrive in multiple waves."}
              </p>
              <p className="mt-2 text-xs font-semibold opacity-90">
                {lang === "es" ? "Guía completa: Planes de Evacuación → capítulo Tsunami." : "Full guidance: Evacuation Plans → Tsunami chapter."}
              </p>
            </div>
          )}
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900">{tx(ui.weather.alertsTitle, lang)}</h3>
            <div className="flex items-center gap-2">
              <button
                data-testid="siren-toggle"
                onClick={toggleSiren}
                aria-pressed={sirenOn}
                title={lang === "es" ? "Sirena al llegar un aviso nuevo" : "Siren when a new alert arrives"}
                className={`inline-flex items-center gap-2 rounded-md border-2 px-3 py-2.5 text-sm font-bold transition-colors duration-200 active:scale-[0.98] ${
                  sirenOn
                    ? "border-red-600 bg-red-600 text-white"
                    : "border-slate-300 bg-white text-slate-500 hover:border-red-600 hover:text-red-700"
                }`}
              >
                <Icon name={sirenOn ? "Volume2" : "VolumeX"} className="w-4 h-4" />
                <span className="hidden sm:inline">{lang === "es" ? "Sirena" : "Siren"}</span>
              </button>
              <button
                data-testid="siren-test-btn"
                onClick={playSiren}
                title={lang === "es" ? "Escuchar el sonido de alerta ahora" : "Hear the alert sound now"}
                className="inline-flex items-center gap-1.5 rounded-md border-2 border-slate-300 bg-white px-3 py-2.5 text-sm font-bold text-slate-600 hover:border-red-600 hover:text-red-700 transition-colors duration-200 active:scale-[0.98]"
              >
                <Icon name="Siren" className="w-4 h-4" />
                <span className="hidden sm:inline">{lang === "es" ? "Probar" : "Test"}</span>
              </button>
              <button
                data-testid="tsunami-drill-btn"
                onClick={toggleDrill}
                aria-pressed={tsunamiDrill}
                title={lang === "es" ? "Simulacro: ver el banner de tsunami y practicar" : "Drill: see the tsunami banner and practice"}
                className={`inline-flex items-center gap-1.5 rounded-md border-2 px-3 py-2.5 text-sm font-bold transition-colors duration-200 active:scale-[0.98] ${
                  tsunamiDrill
                    ? "border-red-700 bg-red-700 text-white"
                    : "border-slate-300 bg-white text-slate-500 hover:border-red-700 hover:text-red-700"
                }`}
              >
                <Icon name="Waves" className="w-4 h-4" />
                <span className="hidden sm:inline">{lang === "es" ? "Simulacro" : "Drill"}</span>
              </button>
            <button
              data-testid="push-alerts-toggle"
              onClick={pushState === "on" ? disablePush : enablePush}
              disabled={pushState === "busy" || pushState === "unsupported"}
              aria-pressed={pushState === "on"}
              className={`inline-flex items-center gap-2 rounded-md border-2 px-4 py-2.5 text-sm font-bold transition-colors duration-200 active:scale-[0.98] disabled:opacity-60 ${
                pushState === "on"
                  ? "border-green-700 bg-green-700 text-white"
                  : "border-amber-600 bg-white text-amber-800 hover:bg-amber-600 hover:text-white"
              }`}
            >
              <Icon name={pushState === "on" ? "BellOff" : "Bell"} className="w-4 h-4" />
              {pushState === "on"
                ? (lang === "es" ? "Alertas activadas" : "Alerts on")
                : pushState === "busy"
                  ? (lang === "es" ? "Activando…" : "Enabling…")
                  : (lang === "es" ? "Notificarme de avisos NWS" : "Notify me of NWS alerts")}
            </button>
          </div>
          </div>
          {(pushState === "denied" || pushState === "error" || pushState === "unsupported") && (
            <p data-testid="push-alerts-note" className="mb-3 text-sm font-semibold text-red-700">
              {pushState === "denied"
                ? (lang === "es" ? "Permiso de notificaciones denegado. Actívelo en la configuración del navegador." : "Notification permission denied. Enable it in browser settings.")
                : pushState === "unsupported"
                  ? (lang === "es" ? "Este navegador no admite notificaciones push." : "This browser does not support push notifications.")
                  : (lang === "es" ? "No pudimos activar las notificaciones. Intente de nuevo." : "Could not enable notifications. Try again.")}
            </p>
          )}
          {newAlert && (
            <div data-testid="new-alert-banner" className="mb-3 flex items-center justify-between gap-3 rounded-lg border-2 border-red-700 bg-red-600 px-4 py-3 text-white shadow-lg">
              <p className="flex items-center gap-2 text-sm font-bold">
                <Icon name="Siren" className="w-5 h-5 shrink-0" />
                {lang === "es" ? "¡Nuevo aviso del NWS recibido! Revisa los detalles abajo." : "New NWS alert received! See details below."}
              </p>
              <button
                data-testid="new-alert-banner-close"
                onClick={() => setNewAlert(false)}
                aria-label={lang === "es" ? "Cerrar" : "Close"}
                className="shrink-0 rounded p-1 hover:bg-red-700 transition-colors duration-150"
              >
                <Icon name="X" className="w-4 h-4" />
              </button>
            </div>
          )}
          {alerts.length === 0 ? (
            <div data-testid="no-alerts-card" className="rounded-lg border border-green-700 bg-green-50 p-5 flex items-center gap-3">
              <Icon name="CheckCircle2" className="w-6 h-6 text-green-700 shrink-0" />
              <p className="text-sm sm:text-base font-semibold text-green-900">{tx(ui.weather.noAlerts, lang)}</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {alerts.map((a, i) => (
                <div key={a.properties?.id || i} data-testid={`alert-card-${i}`} className="rounded-lg border-l-4 border-red-600 border border-slate-300 bg-red-50 p-5">
                  <p className="flex items-center gap-2 font-bold text-red-800">
                    <Icon name="TriangleAlert" className="w-5 h-5" /> {a.properties?.event}
                  </p>
                  <p className="mt-1.5 text-sm text-slate-700">{a.properties?.headline || a.properties?.description?.slice(0, 300)}</p>
                  <a
                    data-testid={`alert-share-wa-${i}`}
                    href={`https://wa.me/?text=${encodeURIComponent(waAlertText(a, lang))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 rounded-md border-2 border-green-700 px-3 py-1.5 text-xs font-bold text-green-700 hover:bg-green-700 hover:text-white transition-colors duration-150 active:scale-[0.97]"
                  >
                    <Icon name="MessageCircle" className="w-3.5 h-3.5" /> WhatsApp
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Marquee />

      <section className="px-4 sm:px-8 lg:px-12 py-8 sm:py-10">
        <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 xl:grid-cols-4">
          <StatusCard
            testId="status-hurricanes" icon="Wind" tone={hurricaneAlerts.length ? "red" : seasonOn ? "amber" : "green"}
            title={tx(S.hurricanes, lang)}
            big={hurricaneAlerts.length ? `${hurricaneAlerts.length} ${tx(S.activeAlerts, lang)}` : tx(S.noThreat, lang)}
            sub={seasonOn ? tx(S.seasonOn, lang) : tx(S.seasonOff, lang)}
          />
          <StatusCard
            testId="status-earthquakes" icon="Activity" tone="navy"
            title={tx(S.earthquakes, lang)}
            big={tx(S.noThreat, lang)}
            sub={tx(S.quakeText, lang)}
          />
          <StatusCard
            testId="status-floods" icon="Waves" tone={floodAlerts.length ? "red" : "green"}
            title={tx(S.floods, lang)}
            big={floodAlerts.length ? `${floodAlerts.length} ${tx(S.activeAlerts, lang)}` : tx(S.noThreat, lang)}
            sub={lang === "es" ? "Nunca cruce corrientes de agua." : "Never cross moving water."}
          />
          <StatusCard
            testId="status-climate" icon="Thermometer" tone="navy"
            title={tx(S.climate, lang)}
            big="+1.5°C"
            sub={tx(S.climateText, lang)}
          />
        </div>
      </section>
    </div>
  );
}
